import {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  forwardRef,
  ForwardedRef,
  HTMLAttributes,
} from 'react';

import Link, { LinkProps } from '../Link';
import Text from '../Text';
import { useBaseCta } from './useBaseCta';

type AnchorProps = {
  tag: 'a';
  ref?: ForwardedRef<HTMLAnchorElement>;
} & LinkProps;

type ButtonProps = {
  tag: 'button';
  ref?: ForwardedRef<HTMLButtonElement>;
  onClick: MouseEventHandler;
  editable?: boolean;
} & (
  | {
      text: string | undefined;
      children?: ReactNode;
    }
  | {
      text?: string | undefined;
      children?: ReactNode;
    }
) &
  ButtonHTMLAttributes<HTMLButtonElement>;

type SpanProps = {
  tag: 'span';
  text?: string;
  children?: ReactNode;
} & HTMLAttributes<HTMLSpanElement>;

export type BaseCtaProps = AnchorProps | ButtonProps | SpanProps;

const BaseCta = forwardRef<HTMLAnchorElement | HTMLButtonElement, BaseCtaProps>(
  function BaseCtaComponent(props: BaseCtaProps, ref): JSX.Element {
    const { shouldRenderCta } = useBaseCta();

    if (!shouldRenderCta(props)) {
      return <></>;
    }

    if (props.tag === 'a') {
      const { children, className, tag, ...otherProps } = props;

      return (
        <Link className={className} {...otherProps} ref={ref as ForwardedRef<HTMLAnchorElement>}>
          {children}
        </Link>
      );
    }

    if (props.tag === 'span') {
      const { children, className, text, ...otherProps } = props;
      if (!children && !text) return <></>;
      return (
        <span className={className} {...otherProps}>
          {children || text}
        </span>
      );
    }

    const { children, editable, className, text, onClick, tag, ...otherProps } = props;
    return (
      <button
        className={className}
        onClick={onClick}
        {...otherProps}
        ref={ref as ForwardedRef<HTMLButtonElement>}
      >
        {children || (typeof text === 'string' ? <Text field={text} /> : '')}
      </button>
    );
  }
);

export default BaseCta;
